import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSelectedNodesWithTypeId } from './util';
import { createPortfolioItemTree } from 'services/portfolios/service';
import { Skeleton, Flex, Box, Text, useMediaQuery } from '@chakra-ui/react';
import { ItemsSelect } from 'components/portfolios/ItemsSelect';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { PortfolioItemTreeNode } from 'types/portfolios';
import PortfolioContents from './PortfolioContents';
import { AddToPortfolioButton } from './AddToPortfolioButton';
import { updateNodeInTree } from 'components/react-tree/utils-ts';
import { FormattedMessage } from 'react-intl';
import { HelperText } from './HelperText';
import InfoPopover from 'components/forms/InfoPopover';
import { useWizardNavigation } from 'components/wizard/hooks/useWizardNavigation';
import { useWizardState } from 'components/wizard/hooks/useWizardState';

interface PortfolioItemSelectStepProps {
    stepId: string;
    itemTypeIds: number[];
    // list of ids that should be selected by default with selection disabled
    defaultSelectedIds?: (number | string)[];
    expandByDefault?: boolean;
}

/**
 * PortfolioItemSelectStep allows users to select items from a hierarchical tree to add to their portfolio.
 * @param stepId - Unique identifier for this wizard step
 * @param itemTypeIds - Array of item type IDs to filter the tree (e.g., [1, 2] for thermal and renewables)
 * @param defaultSelectedIds - Optional items to pre-select and disable (e.g., existing portfolio items)
 * @param expandByDefault - Whether to expand all tree nodes on initial load (default: true)
 */
export function PortfolioItemSelectStep({
    stepId,
    itemTypeIds,
    defaultSelectedIds,
    expandByDefault = true,
}: PortfolioItemSelectStepProps) {
    const mounted = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isDesktop] = useMediaQuery('(min-width: 1400px)');
    const [addingToPortfolio, setAddingToPortfolio] = useState(false);
    const [nodes, setNodes] = useState<PortfolioItemTreeNode[]>();
    const [expanded, setExpanded] = useState<string[]>([]);
    const { items } = useSelector((state: RootState) => state.portfolio);
    const { currentStep, formData, updateStepFormData } = useWizardState();
    const { validateAndSetFormData, handleNext } = useWizardNavigation();

    const title = currentStep?.title;
    const info = currentStep?.info;
    const description = currentStep?.description;
    const helperText = currentStep?.helperText;

    const selected = useMemo(() => {
        if (!nodes) {
            return [];
        }
        return getSelectedNodesWithTypeId(nodes, itemTypeIds);
    }, [nodes, itemTypeIds]);

    const handleAddToPortfolio = useCallback(async () => {
        setAddingToPortfolio(true);
        await validateAndSetFormData({
            selected: selected,
        });
        setAddingToPortfolio(false);
    }, [selected, validateAndSetFormData]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        await handleNext({
            selected: selected,
        });
    };

    useEffect(() => {
        if (mounted.current) {
            return;
        }

        const init = async () => {
            setIsLoading(true);
            let tree = await createPortfolioItemTree(items, itemTypeIds);

            // update tree with default selected values and disable them
            const initialValues = formData[stepId]?.selected ?? [];
            for (const value of initialValues) {
                tree = updateNodeInTree(tree, String(value?.id), (node) => ({
                    isSelected: true,
                }));
            }

            // update tree with default selected values and disable them
            if (defaultSelectedIds) {
                for (const id of defaultSelectedIds) {
                    tree = updateNodeInTree(tree, String(id), (node) => ({
                        isSelected: true,
                        isDisabled: true,
                    }));
                }
            }

            const defaultSelected = getSelectedNodesWithTypeId(tree, itemTypeIds);
            const selectedValues = new Set([...initialValues, ...defaultSelected]);

            // Save default selected items and previously selected values to form data
            await updateStepFormData({
                selected: Array.from(selectedValues),
            });

            setNodes(tree);
            setExpanded(expandByDefault && tree.length > 0 ? tree.map((node) => node.id) : []);
        };

        init().finally(() => {
            setIsLoading(false);
            mounted.current = true;
        });
    }, [formData, items, itemTypeIds, stepId, expandByDefault, updateStepFormData, defaultSelectedIds]);

    if (isLoading) {
        return <Skeleton height="40px" />;
    }

    return (
        <form id={`form-${stepId}`} onSubmit={handleSubmit}>
            <Flex h="full" w="full" flexDir="column">
                <Flex flexDir="column" flexShrink={0} mb={5}>
                    {description && (
                        <Text fontSize="larger" mb={4} color="black">
                            {typeof description === 'string' ? description : <FormattedMessage {...description} />}
                        </Text>
                    )}
                    {helperText && (
                        <HelperText>
                            {typeof helperText === 'string' ? helperText : <FormattedMessage {...helperText} />}
                        </HelperText>
                    )}
                </Flex>
                <Flex flexDirection={isDesktop ? 'row' : 'column'} gap={2} pb={4}>
                    <Box flex="1">
                        {nodes ? (
                            <>
                                <Flex gap={2} mb={4} alignItems={'center'}>
                                    {title && (
                                        <Text fontSize="lg" fontWeight="semibold" color="black">
                                            {typeof title === 'string' ? title : <FormattedMessage {...title} />}
                                        </Text>
                                    )}
                                    {info && (
                                        <InfoPopover
                                            width={isDesktop ? '2xl' : 'sm'}
                                            info={typeof info === 'string' ? info : <FormattedMessage {...info} />}
                                        />
                                    )}
                                </Flex>
                                <ItemsSelect
                                    nodes={nodes}
                                    setNodes={setNodes}
                                    expanded={expanded}
                                    setExpanded={setExpanded}
                                />
                                <AddToPortfolioButton onClick={handleAddToPortfolio} isLoading={addingToPortfolio} />
                            </>
                        ) : (
                            <Text>No items to select</Text>
                        )}
                    </Box>
                    <Box flex="1">
                        <PortfolioContents />
                    </Box>
                </Flex>
            </Flex>
        </form>
    );
}
